import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = { name: "", headline: "", discount_percent: 20, active: false, starts_at: null, ends_at: null, product_ids: [], category_slugs: [] };

export default function AdminSaleBanners() {
    const [items, setItems] = useState([]);
    const [cats, setCats] = useState([]);
    const [products, setProducts] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const load = async () => {
        const [b, c, p] = await Promise.all([api.get("/admin/sale-banners"), api.get("/categories"), api.get("/admin/products")]);
        setItems(b.data); setCats(c.data); setProducts(p.data);
    };
    useEffect(() => { load(); }, []);
    const save = async () => {
        try {
            const p = { ...editing, discount_percent: Number(editing.discount_percent) };
            if (editing.id) await api.put(`/admin/sale-banners/${editing.id}`, p);
            else await api.post("/admin/sale-banners", p);
            toast.success("Saved"); setOpen(false); load();
        } catch { toast.error("Failed"); }
    };
    const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/sale-banners/${id}`); load(); };
    const toggle = async (b) => { await api.put(`/admin/sale-banners/${b.id}`, { ...b, active: !b.active }); load(); };

    return (
        <div data-testid="admin-banners">
            <div className="flex items-center justify-between mb-6">
                <h1 className="font-display text-4xl tracking-tighter">Sale Banners</h1>
                <button onClick={() => { setEditing({...empty}); setOpen(true); }} className="bg-black text-white px-4 py-2 font-mono text-xs uppercase" data-testid="new-banner-btn">+ New Banner</button>
            </div>
            <div className="space-y-3">
                {items.map(b => (
                    <div key={b.id} className="bg-white border-2 border-black p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                            <div className="font-display text-2xl">{b.name}</div>
                            <div className="font-mono text-xs text-neutral-500">{b.headline} — {b.discount_percent}% off</div>
                            <div className="font-mono text-[10px] uppercase mt-1">Products: {b.product_ids?.length || 0} | Categories: {b.category_slugs?.length || 0}</div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => toggle(b)} data-testid={`toggle-banner-${b.id}`} className={`px-3 py-2 font-mono text-xs uppercase border-2 border-black ${b.active ? "bg-[hsl(var(--accent))] text-white" : "bg-white"}`}>{b.active ? "Live" : "Off"}</button>
                            <button onClick={() => { setEditing({...b}); setOpen(true); }} className="px-3 py-2 font-mono text-xs uppercase border-2 border-black">Edit</button>
                            <button onClick={() => del(b.id)} className="px-3 py-2 font-mono text-xs uppercase border-2 border-black">Delete</button>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="border-2 border-black p-10 text-center font-mono text-xs">No banners yet.</div>}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="border-2 border-black max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-display text-2xl">{editing?.id ? "Edit" : "New"} Sale Banner</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <input placeholder="Sale name (e.g., Monsoon Sale)" value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})} className="w-full border-2 border-black p-2 font-mono text-sm" data-testid="banner-name" />
                            <input placeholder="Headline (e.g., Up to 30% off)" value={editing.headline} onChange={e => setEditing({...editing, headline: e.target.value})} className="w-full border-2 border-black p-2 font-mono text-sm" data-testid="banner-headline" />
                            <input type="number" placeholder="Discount percent" value={editing.discount_percent} onChange={e => setEditing({...editing, discount_percent: e.target.value})} className="w-full border-2 border-black p-2 font-mono text-sm" />

                            <div>
                                <div className="font-mono text-[10px] uppercase mb-1">Include categories</div>
                                <div className="flex flex-wrap gap-2">
                                    {cats.map(c => {
                                        const on = (editing.category_slugs || []).includes(c.slug);
                                        return <button key={c.id} type="button" onClick={() => setEditing({...editing, category_slugs: on ? editing.category_slugs.filter(s => s !== c.slug) : [...(editing.category_slugs || []), c.slug]})}
                                            className={`px-3 py-1 border-2 border-black font-mono text-[10px] uppercase ${on ? "bg-black text-white" : "bg-white"}`}>{c.name}</button>;
                                    })}
                                </div>
                            </div>

                            <div>
                                <div className="font-mono text-[10px] uppercase mb-1">Include specific products</div>
                                <div className="max-h-48 overflow-auto border-2 border-black">
                                    {products.map(p => {
                                        const on = (editing.product_ids || []).includes(p.id);
                                        return (
                                            <label key={p.id} className="flex items-center gap-2 p-2 border-b border-neutral-200 font-mono text-xs">
                                                <input type="checkbox" checked={on} onChange={() => setEditing({...editing, product_ids: on ? editing.product_ids.filter(id => id !== p.id) : [...(editing.product_ids || []), p.id]})} />
                                                {p.name}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            <label className="flex items-center gap-2"><input type="checkbox" checked={editing.active} onChange={e => setEditing({...editing, active: e.target.checked})} /> <span className="font-mono text-xs uppercase">Live</span></label>
                        </div>
                    )}
                    <DialogFooter><button onClick={save} className="bg-black text-white px-4 py-2 font-mono text-xs uppercase" data-testid="save-banner-btn">Save</button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
